/**
 * common/storage.js — namespaced persistence (localStorage with in-memory fallback)
 * plus a small `session` helper for the auth token + cached user.
 */
import CONFIG from "../config/config.js";

const mem = new Map(); // fallback when localStorage is unavailable (SSR, privacy mode)

function backend() {
  try {
    const t = "__prompto_probe__";
    window.localStorage.setItem(t, "1");
    window.localStorage.removeItem(t);
    return window.localStorage;
  } catch (e) { return null; }
}
const LS = backend();

export const storage = {
  get(key) {
    try {
      const raw = LS ? LS.getItem(key) : (mem.has(key) ? mem.get(key) : null);
      return raw == null ? null : JSON.parse(raw);
    } catch (e) { return null; }
  },
  set(key, value) {
    const raw = JSON.stringify(value);
    if (LS) LS.setItem(key, raw); else mem.set(key, raw);
  },
  remove(key) {
    if (LS) LS.removeItem(key); else mem.delete(key);
  },
};

export const session = {
  getToken() { return storage.get(CONFIG.TOKEN_STORAGE_KEY); },
  setToken(token) { storage.set(CONFIG.TOKEN_STORAGE_KEY, token); },
  getUser() { return storage.get(CONFIG.USER_STORAGE_KEY); },
  setUser(user) { storage.set(CONFIG.USER_STORAGE_KEY, user); },
  isAuthed() { return !!this.getToken(); },
  clear() {
    storage.remove(CONFIG.TOKEN_STORAGE_KEY);
    storage.remove(CONFIG.USER_STORAGE_KEY);
  },
};

export const anonymous = {
  getID() {
    const key = "prompto.anonymous.id";
    let id = storage.get(key);
    if (typeof id === "string" && id) return id;
    const bytes = new Uint8Array(12);
    try { window.crypto?.getRandomValues?.(bytes); } catch (e) {}
    id = Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("") || String(Date.now());
    storage.set(key, id);
    return id;
  },
};

export default storage;
